import { loginSchema, type LoginInput, type MeResponse } from '@crm/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Eye, EyeOff, LogIn } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { api, ApiClientError } from '@/lib/api'
import { fetchMe, ME_QUERY_KEY } from '@/lib/auth-query'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
  beforeLoad: async ({ context, search }) => {
    if (await fetchMe(context.queryClient)) {
      throw redirect({ to: search.redirect ?? '/' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const [formError, setFormError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const mutation = useMutation({
    mutationFn: (input: LoginInput) => api.post<MeResponse>('/api/auth/login', input),
    onSuccess: (data) => {
      queryClient.setQueryData(ME_QUERY_KEY, data)
      navigate({ to: search.redirect ?? '/' })
    },
    onError: (error) => {
      setFormError(null)
      if (error instanceof ApiClientError) {
        if (error.fields) {
          for (const [field, messages] of Object.entries(error.fields)) {
            if (field === 'email' || field === 'password') {
              setError(field, { message: messages[0] })
            }
          }
          return
        }
        setFormError(error.message)
        return
      }
      setFormError('Something went wrong. Please try again.')
    },
  })

  return (
    <div className="flex min-h-screen">
      <div className="hidden flex-1 items-center justify-center bg-gradient-to-br from-[var(--color-primary)]/5 via-[var(--color-accent)]/5 to-[var(--color-primary)]/10 p-12 lg:flex">
        <div className="max-w-md space-y-6">
          <div className="space-y-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-primary)] shadow-lg shadow-[var(--color-primary)]/20">
              <LogIn className="h-6 w-6 text-[var(--color-primary-foreground)]" />
            </div>
            <h2 className="text-3xl font-semibold tracking-tight">Agency CRM</h2>
            <p className="text-base text-[var(--color-muted-foreground)] leading-relaxed">
              Manage your clients, campaigns, and communications — all in one place.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Client tracking', desc: 'Full lifecycle management' },
              { label: 'Campaigns', desc: 'Multi-channel messaging' },
              { label: 'Smart alerts', desc: 'Real-time notifications' },
              { label: 'Analytics', desc: 'Data-driven insights' },
            ].map((f) => (
              <div
                key={f.label}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/50 p-4 backdrop-blur-sm"
              >
                <p className="text-sm font-semibold">{f.label}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center bg-[var(--color-muted)] px-4">
        <Card className="w-full max-w-sm p-8">
          <div className="mb-8 space-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Sign in to your account to continue
            </p>
          </div>

          <form
            className="space-y-5"
            onSubmit={handleSubmit((values) => {
              setFormError(null)
              mutation.mutate(values)
            })}
            noValidate
          >
            {formError && (
              <div
                role="alert"
                className="rounded-lg bg-[var(--color-destructive-muted)] px-3 py-2.5 text-sm font-medium text-[var(--color-destructive)]"
              >
                {formError}
              </div>
            )}

            <Field label="Email" htmlFor="email" error={errors.email?.message} required>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                aria-invalid={!!errors.email}
                {...register('email')}
              />
            </Field>

            <Field label="Password" htmlFor="password" error={errors.password?.message} required>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            <Button type="submit" className="w-full" size="lg" loading={mutation.isPending}>
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-[var(--color-muted-foreground)]">
            Secure login · Session expires after inactivity
          </p>
        </Card>
      </div>
    </div>
  )
}
